import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import type { StripeCardElementOptions } from "@stripe/stripe-js";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { InputGroup, InputGroupInput } from "@/ds/shadcn/components/ui/input-group";
import { Button } from "@/ds/shadcn/components/ui/button";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useDarkModeInformation } from "@/lib/common/DarkModeContext";
import { derivePaymentCardId } from "@/features/paymentCards/derivePaymentCardId";
import type { PaymentCardModel } from "@/features/paymentCards/models/PaymentCardModel";
import { usePaymentCardSetupIntent } from "@/features/paymentCards/api/usePaymentCardSetupIntent";
import { toast } from "sonner";

export interface StripeCardEntryProps {
	onComplete: () => void;
	embedded?: boolean;
}

export const StripeCardEntry = ({ onComplete, embedded = false }: StripeCardEntryProps): React.JSX.Element => {
	const { isEnabled } = useDarkModeInformation();

	const cardElementOptions: StripeCardElementOptions = {
		style: {
			base: {
				fontFamily: "'Geist', sans-serif",
				fontSmoothing: "antialiased",
				color: isEnabled ? "#FAFAFA" : "#0A0A0A",
				"::placeholder": {
					color: isEnabled ? "#A1A1A1" : "#737373"
				},
				fontSize: "14px"
			}
		}
	};
	const [nameOnCard, setNameOnCard] = useState("");

	const queryClient = useQueryClient();

	const [isFocusedOnCardInput, setIsFocusedOnCardInput] = useState(false);
	const [loadingConfirmCardSetup, setLoadingConfirmCardSetup] = useState(false);
	const [errorConfirmCardSetup, setErrorConfirmCardSetup] = useState("");
	const stripeErrorMessage = "There was an error adding your card with Stripe. Please try again later or contact support for assistance.";

	const stripe = useStripe();
	const elements = useElements();
	const paymentCardSetupIntent = usePaymentCardSetupIntent();

	const sanitizedName = nameOnCard.trim();
	const setupIntentClientSecret = paymentCardSetupIntent.isSuccess ? paymentCardSetupIntent.data.body.setupIntentClientSecret : undefined;

	useEffect(() => {
		setErrorConfirmCardSetup("");

		if (!paymentCardSetupIntent.data) {
			paymentCardSetupIntent.mutate(undefined);
			setNameOnCard("");
		}
	}, []);

	const handleSubmit = async () => {
		if (!stripe || !elements || !sanitizedName || !setupIntentClientSecret) {
			return;
		}

		const cardElement = elements.getElement(CardElement);
		if (!cardElement) {
			return;
		}

		setLoadingConfirmCardSetup(true);

		try {
			const result = await stripe.confirmCardSetup(setupIntentClientSecret, {
				payment_method: {
					card: cardElement,
					billing_details: {
						name: sanitizedName
					}
				}
			});

			if (result.error) {
				setErrorConfirmCardSetup(result.error.message || "Stripe returned an error.");
				setLoadingConfirmCardSetup(false);
				return;
			}

			if (result.setupIntent.status !== "succeeded") {
				setErrorConfirmCardSetup(stripeErrorMessage);
				setLoadingConfirmCardSetup(false);
				return;
			}

			const paymentMethodId = result.setupIntent.payment_method;

			if (paymentMethodId !== null) {
				queryClient.setQueryData(["paymentCards"], (oldData: PaymentCardModel[] | undefined) => {
					if (!oldData) {
						return [];
					}
					return [...oldData, {
						paymentCardId: typeof paymentMethodId === "string" ? derivePaymentCardId(paymentMethodId) : derivePaymentCardId(paymentMethodId.id),
						last4: "●●●●",
						expiry: dayjs(0).toDate(),
						provider: "Unknown"
					}];
				});
			}

			setLoadingConfirmCardSetup(false);
			toast.success("Payment Card Added", { position: "bottom-center" });
			onComplete();
			cardElement.clear();
			paymentCardSetupIntent.reset();
			setTimeout(() => queryClient.invalidateQueries({ queryKey: ["paymentCards"] }), 3000);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		} catch (error) {
			setErrorConfirmCardSetup(stripeErrorMessage);
		}

		setLoadingConfirmCardSetup(false);
	};

	return (
		<div className={`flex flex-col content-normal items-center justify-center w-full ${embedded ? "" : "@md:p-10"}
			${paymentCardSetupIntent.isPending || loadingConfirmCardSetup ? "opacity-50 pointer-events-none" : ""}`}>

			<div className={`relative flex flex-col p-3 gap-3 w-full ${embedded ? "border rounded-lg" : "@md:border @md:rounded-lg max-w-95"}`}>
				{
					(paymentCardSetupIntent.isPending || loadingConfirmCardSetup) &&
						<div className="absolute inset-0 z-50 flex flex-col items-center justify-center">
							<Spinner className="w-10 h-10" />
						</div>
				}

				<div className="type-title text-center">Add Payment Card</div>
				<div className="type-description text-center mb-2">Confirming your card details will not cause payment to be taken.</div>
				<InputGroup>
					<InputGroupInput placeholder="Card Holder Name" value={nameOnCard} onChange={e => setNameOnCard(e.currentTarget.value)} />
				</InputGroup>

				<div className={`group/input-group pl-3 pr-3 pt-2.5 pb-2.5 border-input dark:bg-input/30 rounded-md border shadow-xs transition-[color,box-shadow] outline-none min-w-0 ${isFocusedOnCardInput ? "border-ring ring-ring/50 ring-[3px] shadow-lg" : ""}`}>
					<CardElement
						options={cardElementOptions}
						onFocus={() => setIsFocusedOnCardInput(true)}
						onBlur={() => setIsFocusedOnCardInput(false)} />
				</div>

				<Button className="w-full" variant="outline" onClick={handleSubmit}>
					Add Card
				</Button>

				<div className="type-meta text-right">Powered by Stripe</div>
				<ErrorDisplay error={errorConfirmCardSetup} />
			</div>
		</div>
	);
};
